<template>
  <v-dialog
      v-model="dialog"
      :width="dialogWidth"
      @keydown.esc="cancel"
      @keydown.enter.prevent="handleEnter"
  >
    <v-card
        class="resizable-card"
        :class="{ 'no-select': resizing }"
        :style="{ height: dialogHeight + 'px' }"
    >
      <v-card-title>
        <span class="headline">Patch Builder</span>
      </v-card-title>

      <v-card-text>
        <div class="mb-3">
          <strong>Current Config ID:</strong>
          <span class="text--secondary">{{ currConfigUID }}</span>
        </div>

        <v-autocomplete
            v-model="selectedConfig"
            :items="availableConfigs"
            :loading="isLoading"
            dense
            outlined
            item-text="name"
            item-value="uid"
            return-object
            hide-details
            label="Select base config to compare with"
            placeholder="Choose a config..."
        >
          <template #item="{ item }">
            <v-list-item-content>
              <v-list-item-title>{{ item.name }}</v-list-item-title>
              <v-list-item-subtitle>{{ item.uid }}</v-list-item-subtitle>
            </v-list-item-content>
          </template>
        </v-autocomplete>

        <div v-if="errorMessage" class="error--text mt-2">
          {{ errorMessage }}
        </div>
      </v-card-text>

      <v-card-actions class="resizable-card__actions">
        <v-spacer></v-spacer>
        <v-btn text @click="cancel" :disabled="isLoading">
          Cancel
        </v-btn>
        <v-btn
            color="success"
            @click="buildPatch"
            :disabled="!canBuildPatch"
            :loading="isLoading"
        >
          Build Patch
        </v-btn>
      </v-card-actions>

      <div class="resize-handle" @mousedown="startResize"></div>
    </v-card>
  </v-dialog>
</template>

<script>
import { saveAs } from "file-saver";
import { RedactorUtils } from "@/modules/redactor/core/RedactorUtils";
import { generatePatch } from "@/common/PatchBuilder";

export default {
  name: "r-patch-builder-dialog",

  data() {
    return {
      dialog: false,
      selectedConfig: null,
      currConfigUID: RedactorUtils.getConfigUID(),
      availableConfigs: [],
      isLoading: false,
      errorMessage: null,
      dialogWidth: 575,
      dialogHeight: 338,
      resizing: false,
      resizeStartX: 0,
      resizeStartY: 0,
      resizeStartWidth: 575,
      resizeStartHeight: 338,
      viewportPadding: 32
    };
  },

  computed: {
    canBuildPatch() {
      return !this.isLoading && this.selectedConfig?.uid;
    }
  },

  watch: {
    dialog(isOpen) {
      if (isOpen) {
        this.loadConfigs();
      } else {
        this.reset();
      }
    }
  },

  methods: {
    show() {
      this.dialog = true;
    },

    async loadConfigs() {
      try {
        this.isLoading = true;
        this.availableConfigs = RedactorUtils.getGlobalConfigs() || [];
        this.errorMessage = null;
      } catch (error) {
        console.error("Failed to load configs:", error);
        this.errorMessage = "Failed to load configurations";
      } finally {
        this.isLoading = false;
      }
    },

    reset() {
      this.selectedConfig = null;
      this.errorMessage = null;
      this.isLoading = false;
      this.stopResize();
    },

    async buildPatch() {
      if (!this.canBuildPatch) return;

      try {
        this.isLoading = true;
        this.errorMessage = null;

        const [baseConfig, currentConfig] = await Promise.all([
          RedactorUtils.getConfigData(this.selectedConfig.uid),
          RedactorUtils.getConfigData(this.currConfigUID)
        ]);

        if (!baseConfig || !currentConfig) {
          throw new Error("Failed to load configurations");
        }

        const patch = generatePatch(baseConfig, currentConfig);

        if (this.isEmpty(patch)) {
          this.$alert("Patch not created. Configurations are identical.");
          return;
        }

        this.downloadPatch(patch);
        this.dialog = false;
      } catch (error) {
        console.error("Failed to build patch:", error);
        this.errorMessage = `Error: ${error.message}`;
      } finally {
        this.isLoading = false;
      }
    },

    isEmpty(patch) {
      return Object.values(patch).every(
          section => !section || Object.keys(section).length === 0
      );
    },

    downloadPatch(patch) {
      const blob = new Blob([JSON.stringify(patch, null, 2)], {
        type: "application/json"
      });

      const filename = `patch_${this.selectedConfig.uid}_to_${this.currConfigUID}.json`;
      saveAs(blob, filename);
    },

    cancel() {
      if (!this.isLoading) {
        this.dialog = false;
      }
    },

    handleEnter() {
      if (this.canBuildPatch) {
        this.buildPatch();
      } else {
        this.cancel();
      }
    },

    startResize(e) {
      this.resizing = true;
      this.resizeStartX = e.clientX;
      this.resizeStartY = e.clientY;
      this.resizeStartWidth = this.dialogWidth;
      this.resizeStartHeight = this.dialogHeight;
      window.addEventListener("mousemove", this.onResize);
      window.addEventListener("mouseup", this.stopResize);
    },

    onResize(e) {
      if (!this.resizing) return;
      const dx = e.clientX - this.resizeStartX;
      const dy = e.clientY - this.resizeStartY;
      let newWidth = this.resizeStartWidth + dx;
      let newHeight = this.resizeStartHeight + dy;
      const minWidth = 420;
      const minHeight = 220;
      if (newWidth < minWidth) newWidth = minWidth;
      if (newHeight < minHeight) newHeight = minHeight;
      this.dialogWidth = newWidth;
      this.dialogHeight = newHeight;
    },

    stopResize() {
      if (!this.resizing) return;
      this.resizing = false;
      window.removeEventListener("mousemove", this.onResize);
      window.removeEventListener("mouseup", this.stopResize);
    }
  },

  beforeDestroy() {
    window.removeEventListener("mousemove", this.onResize);
    window.removeEventListener("mouseup", this.stopResize);
  }
};
</script>

<style scoped>
.v-card-text {
  padding-bottom: 0;
}

.error--text {
  font-size: 0.875rem;
}

.resizable-card {
  position: relative;
  display: flex;
  flex-direction: column;
}

.resizable-card__actions {
  flex-shrink: 0;
}

.resize-handle {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 14px;
  height: 14px;
  cursor: se-resize;
  border-right: 2px solid rgba(0, 0, 0, 0.3);
  border-bottom: 2px solid rgba(0, 0, 0, 0.3);
}

.no-select {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}
</style>